import { buddyClient, qs } from './client';

/**
 * H3: nyce.buddy setup — organizations, tenants (= installations), systems (= registrations of an installation for one
 * organization), database servers, token limits, global engine settings and model pricing. Contracts reviewed from
 * buddy3's `packages/contracts/src/admin.ts` (docs/contracts.md "Admin"); only what the setup pages use. Secrets
 * (service password, AUTH service key, DB server password) are write-only: buddy3 reports `has…` flags, never values.
 */

export type ThinkingLevel = 'normal' | 'high' | 'expert';

/** Engine settings cascade global → tenant → organization → system; missing keys inherit from the level above. */
export interface ModelSettings {
  model: string;
  routeModel: string;
  /** The model of the chat picker's High / Expert level; absent = `model` (buddy3 ADR-0021). */
  highModel?: string;
  expertModel?: string;
  thinking: string;
  thinkingHigh?: string;
  thinkingExpert?: string;
  maxSteps: number;
  targetSteps: number;
  maxStepsHigh?: number;
  targetStepsHigh?: number;
  maxStepsExpert?: number;
  targetStepsExpert?: number;
  maxRouteFiles: number;
  sampleRows: number;
  sampleChars: number;
  maxQueryRows: number;
  schemaMode: 'full' | 'targeted';
  html: boolean;
  htmlMode: 'off' | 'sparing' | 'free';
  clarify: boolean;
  queryLogs: boolean;
  searchDocs: boolean;
  docLinks: 'internal' | 'external';
  prefixMode: 'auto' | 'routed' | 'full';
  explicitCache: 'auto' | 'off' | 'on';
  location: string;
  timezone: string;
  defaultLanguage: string;
  defaultThinking: ThinkingLevel;
  /** Kill switch of the monitor scheduler (global only; default on). */
  enableAgenticMonitoring?: boolean;
}

export type ModelSettingsOverrides = Partial<ModelSettings>;

/** Feature flag overrides of one level; an absent flag inherits. */
export type FeatureOverrides = Record<string, boolean>;

/** `GET/PUT /global-settings`: the roots of both cascades; PUT answers 409 on a stale `version`. */
export interface GlobalSettings {
  settings: ModelSettings;
  features?: FeatureOverrides;
  /** USD per 1 000 000 NYCE tokens (ADR-0009). */
  nyceTokenRatePerMillion?: number;
  version: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface GlobalSettingsInput {
  settings?: ModelSettingsOverrides;
  features?: FeatureOverrides;
  nyceTokenRatePerMillion?: number;
  version?: number;
}

export interface Organization {
  /** The AUTH customer id (the `orgid` claim). */
  id: string;
  name: string;
  enabled: boolean;
  settings?: ModelSettingsOverrides;
  features?: FeatureOverrides;
  tenantCount?: number;
  systemCount?: number;
  monthlyLimit?: number | null;
  isUnlimited?: boolean;
  usedTokens?: number;
  usagePercentage?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export type OrganizationInput = Pick<Organization, 'id' | 'name' | 'enabled' | 'settings' | 'features'>;

export type LoginFlowSetting = 'auto' | 'token' | 'exchange';

/** An AUTH tenant = one NYCE.LOGIC installation (workserver, service account, logs, database). */
export interface Tenant {
  /** The AUTH tenant id. */
  id: string;
  name: string;
  environment?: string;
  baseUrl?: string;
  apiUrl?: string;
  serviceUser?: string;
  hasServicePassword?: boolean;
  hasServiceKey?: boolean;
  client?: string;
  warehouse?: string;
  clientGroup?: string;
  logNamespace?: string;
  logFilter?: string;
  dbServerId?: string | null;
  dbName?: string;
  loginFlowSetting?: LoginFlowSetting | (string & {});
  /** What `loginFlowSetting` resolves to; read-only. */
  loginFlow?: string;
  secretError?: string | null;
  /** The organizations its systems are billed under; read-only. */
  organizations?: { id: string; name: string }[];
  enabled: boolean;
  settings?: ModelSettingsOverrides;
  features?: FeatureOverrides;
  systemCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** Write shape: the secrets are sent only when typed (absent or empty keeps the stored value). */
export type TenantInput = Omit<
  Tenant,
  | 'hasServicePassword'
  | 'hasServiceKey'
  | 'loginFlow'
  | 'secretError'
  | 'organizations'
  | 'systemCount'
  | 'createdAt'
  | 'updatedAt'
> & { servicePassword?: string; serviceKey?: string };

export interface SchemaState {
  cachedAt?: string | null;
  entities?: number;
  hash?: string;
  source?: string;
  refreshing?: boolean;
  error?: string | null;
}

/** A registration of an installation for one organization; the installation fields are the tenant's (read-only here). */
export interface System {
  id: string;
  name: string;
  environment?: string;
  tenantId: string;
  tenantName?: string;
  organizationId?: string | null;
  organizationName?: string;
  baseUrl?: string;
  authTenantId?: string;
  hasServicePassword?: boolean;
  hasServiceKey?: boolean;
  allowedGroups: string[];
  adminGroups: string[];
  hostnamePatterns: string[];
  allowedOrigins: string[];
  features: FeatureOverrides;
  settings: ModelSettingsOverrides;
  enabled: boolean;
  dbServerId?: string | null;
  dbName?: string;
  schema?: SchemaState;
  available?: boolean;
  error?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** What `POST`/`PUT /systems` accept: the registration only. */
export type SystemInput = Pick<
  System,
  | 'id'
  | 'name'
  | 'tenantId'
  | 'authTenantId'
  | 'organizationId'
  | 'allowedGroups'
  | 'adminGroups'
  | 'hostnamePatterns'
  | 'allowedOrigins'
  | 'features'
  | 'settings'
  | 'enabled'
>;

export interface ConnectionTestResult {
  ok: boolean;
  workserver?: { ok: boolean; version?: string; ms?: number; error?: string };
  auth?: { ok: boolean; ms?: number; error?: string };
  businessinfo?: { ok: boolean; entities?: number; error?: string };
  error?: string;
}

/** `async` = 2026.9+, `legacy` = 2026.6 … 2026.8, `classic` = before 2026.6. */
export type LineFlavor = 'async' | 'legacy' | 'classic';

export interface ProvisionRequest {
  workflow?: boolean;
  groups?: boolean;
  launch?: boolean;
  /** Omitted = detected from the installation's bot/health. */
  flavor?: LineFlavor;
}

export type ProvisionItemState =
  'missing' | 'current' | 'outdated' | 'unknown' | 'created' | 'updated' | 'unchanged' | 'skipped' | 'failed';

export interface ProvisionItem {
  key: string;
  label: string;
  state: ProvisionItemState;
  detail?: string;
}

export interface ProvisionReport {
  ok: boolean;
  applied: boolean;
  host: string;
  database: string;
  databaseVersion?: string;
  flavor: LineFlavor;
  flavorSource?: string;
  items: ProvisionItem[];
  error?: string;
  restartRequired?: boolean;
  runId?: number | null;
}

export interface ProvisionRun {
  id: number;
  at: string;
  user: string;
  flavor: LineFlavor;
  ok: boolean;
  error?: string;
  objects?: { workflow?: boolean; groups?: boolean; launch?: boolean; workflowCode?: string };
  result?: { items?: ProvisionItem[]; restartRequired?: boolean };
}

/** A tenant AUTH knows, for the tenant/system pickers; `matches` = systems on that same workserver. */
export interface AuthTenant {
  id: string;
  name?: string;
  status?: string;
  identityProviderUrl: string;
  customerName?: string;
  systems: string[];
  matches: string[];
}

export interface DbServer {
  id: string;
  name: string;
  cluster: string;
  kind: 'postgres' | 'alloydb' | (string & {});
  host: string;
  port: number;
  username: string;
  hasPassword?: boolean;
  sslMode: 'disable' | 'prefer' | 'require' | (string & {});
  note?: string;
  systemCount?: number;
  secretError?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** `password` omitted keeps the stored one. */
export type DbServerInput = Omit<
  DbServer,
  'hasPassword' | 'systemCount' | 'secretError' | 'createdAt' | 'updatedAt'
> & { password?: string };

export interface DbServerTestResult {
  ok: boolean;
  ms?: number;
  database?: string;
  serverVersion?: string;
  nyceLogic?: boolean | null;
  error?: string;
}

export interface DbServerDatabase {
  name: string;
  /** Carries the NYCE.LOGIC workflow tables; null when it could not be opened. */
  nyceLogic?: boolean | null;
}

/** Budgets since buddy3 0015: organizations and systems (tenant rows are legacy, read-only). */
export type LimitScope = 'organization' | 'system';

export interface TokenLimit {
  scope: LimitScope | 'tenant';
  scopeId: string;
  name?: string;
  tenantId?: string;
  tenantName?: string;
  organizationId?: string | null;
  organizationName?: string;
  /** null = unlimited. */
  monthlyLimit: number | null;
  isUnlimited?: boolean;
  warnPercent?: number | null;
  usedTokens?: number;
  usagePercentage?: number | null;
  updatedAt?: string;
  updatedBy?: string;
  /** false = no row stored yet. */
  stored?: boolean;
}

export interface TokenLimitInput {
  monthlyLimit: number | null;
  isUnlimited?: boolean;
  warnPercent?: number | null;
}

export interface ModelPricing {
  model: string;
  provider?: string;
  /** USD per 1M tokens. */
  inputPrice: number;
  cachedInputPrice?: number;
  outputPrice: number;
  /** Policy multiplier on top of the prices. */
  tokenFactor: number;
  updatedAt?: string;
}

const seg = encodeURIComponent;

/** The setup routes of one environment's nyce.buddy. */
export function setupFor(target: string) {
  const c = buddyClient(target);
  return {
    globalSettings: {
      get: () => c.get<GlobalSettings>('/global-settings'),
      put: (body: GlobalSettingsInput) => c.send<GlobalSettings>('PUT', '/global-settings', body),
    },
    organizations: {
      list: () => c.get<Organization[]>('/organizations'),
      get: (id: string) => c.get<Organization>(`/organizations/${seg(id)}`),
      create: (body: OrganizationInput) => c.send<Organization>('POST', '/organizations', body),
      update: (id: string, body: Partial<OrganizationInput>) =>
        c.send<Organization>('PUT', `/organizations/${seg(id)}`, body),
      remove: (id: string) => c.send<void>('DELETE', `/organizations/${seg(id)}`),
    },
    tenants: {
      list: () => c.get<Tenant[]>('/tenants'),
      get: (id: string) => c.get<Tenant>(`/tenants/${seg(id)}`),
      create: (body: TenantInput) => c.send<Tenant>('POST', '/tenants', body),
      update: (id: string, body: Partial<TenantInput>) => c.send<Tenant>('PUT', `/tenants/${seg(id)}`, body),
      remove: (id: string) => c.send<void>('DELETE', `/tenants/${seg(id)}`),
    },
    authTenants: () => c.get<AuthTenant[]>('/auth-tenants'),
    systems: {
      list: () => c.get<System[]>('/systems'),
      get: (id: string) => c.get<System>(`/systems/${seg(id)}`),
      create: (body: SystemInput) => c.send<System>('POST', '/systems', body),
      update: (id: string, body: Partial<SystemInput>) => c.send<System>('PUT', `/systems/${seg(id)}`, body),
      remove: (id: string) => c.send<void>('DELETE', `/systems/${seg(id)}`),
      /** Body = the form values (optional): tests what is typed, not only what is stored. */
      testConnection: (id: string, body?: Partial<SystemInput>) =>
        c.send<ConnectionTestResult>('POST', `/systems/${seg(id)}/test-connection`, body),
      schema: (id: string) => c.get<SchemaState>(`/systems/${seg(id)}/schema`),
      /** Asynchronous: `refreshing: true` until done (poll `schema`). */
      refreshSchema: (id: string) => c.send<SchemaState>('POST', `/systems/${seg(id)}/refresh-schema`),
      provisionPreview: (id: string, body: ProvisionRequest) =>
        c.send<ProvisionReport>('POST', `/systems/${seg(id)}/provision/preview`, body),
      provision: (id: string, body: ProvisionRequest) =>
        c.send<ProvisionReport>('POST', `/systems/${seg(id)}/provision`, body),
      provisionRuns: (id: string) => c.get<ProvisionRun[]>(`/systems/${seg(id)}/provision/runs`),
      provisioningSql: (id: string) =>
        c.download(`/systems/${seg(id)}/provisioning.sql`, `nycebuddy-provisioning-${id}.sql`),
    },
    dbServers: {
      list: () => c.get<DbServer[]>('/db-servers'),
      create: (body: DbServerInput) => c.send<DbServer>('POST', '/db-servers', body),
      update: (id: string, body: Partial<DbServerInput>) =>
        c.send<DbServer>('PUT', `/db-servers/${seg(id)}`, body),
      remove: (id: string) => c.send<void>('DELETE', `/db-servers/${seg(id)}`),
      databases: (id: string) => c.get<DbServerDatabase[]>(`/db-servers/${seg(id)}/databases`),
      test: (id: string, database?: string) =>
        c.send<DbServerTestResult>('POST', `/db-servers/${seg(id)}/test`, { database }),
    },
    limits: {
      list: (scope: LimitScope) => c.get<TokenLimit[]>(`/limits${qs({ scope })}`),
      put: (scope: LimitScope, id: string, body: TokenLimitInput) =>
        c.send<TokenLimit>('PUT', `/limits/${scope}/${seg(id)}`, body),
    },
    pricing: {
      list: () => c.get<ModelPricing[]>('/model-pricing'),
      upsert: (body: ModelPricing) => c.send<ModelPricing>('POST', '/model-pricing', body),
      remove: (model: string) => c.send<void>('DELETE', `/model-pricing/${seg(model)}`),
    },
  };
}

export type SetupApi = ReturnType<typeof setupFor>;
